<template>
  <div class="simple-resource-management">
    <div class="page-header">
      <div class="header-main">
        <div class="header-left">
          <h1 class="page-title">资源管理</h1>
          <p class="page-description">管理您的求职相关资源</p>
          <el-button type="primary" size="small" class="old-version-btn" @click="returnToOldVersion">
            返回旧版
          </el-button>
        </div>
        <div class="view-toggle">
          <el-radio-group v-model="currentView" size="large" class="custom-radio-group" @change="handleViewChange">
            <el-radio-button label="list" class="rounded-button">
              <el-icon><Grid /></el-icon>
              列表视图
            </el-radio-button>
            <el-radio-button label="dashboard" class="rounded-button">
              <el-icon><DataBoard /></el-icon>
              仪表盘
            </el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <div v-if="currentView === 'list'" class="header-actions">
        <div class="search-box">
          <el-input
            v-model="searchText"
            placeholder="搜索公司..."
            clearable
            class="rounded-search"
            @clear="loadData"
            @keyup.enter="handleSearch"
            size="default"
          >
            <template #prefix>
              <el-icon class="search-icon"><Search /></el-icon>
            </template>
            <template #suffix>
              <el-button 
                circle 
                plain 
                class="search-action-button" 
                @click="handleSearch"
                title="搜索"
              >
                <el-icon><Search /></el-icon>
              </el-button>
            </template>
          </el-input>
        </div>
        <div class="resource-actions">
          <el-button type="primary" @click="openAddDialog" round>
            <el-icon><Plus /></el-icon> 添加记录
          </el-button>
        </div>
      </div>
    </div>

    <!-- 列表视图 -->
    <div v-if="currentView === 'list'" class="table-container">
      <!-- 表格展示区域 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        style="width: 100%"
        row-key="id"
        border
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
        class="rounded-table"
        @row-click="handleRowClick"
      >
        <el-table-column type="selection" width="55" align="center" :selectable="() => false" class-name="selection-column" />
        <el-table-column label="公司名称" prop="companyName" min-width="180">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'companyName')}" @click.stop="handleCellClick(scope.row, {property: 'companyName', label: '公司名称'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'companyName')">
                <el-input 
                  v-model="editingValue" 
                  size="small" 
                  @keydown="handleEditKeydown"
                  ref="editInput"
                  autofocus
                  class="seamless-input"
                />
              </template>
              <span v-else>{{ scope.row.companyName }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="城市" prop="city" width="120">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'city')}" @click.stop="handleCellClick(scope.row, {property: 'city', label: '城市'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'city')">
                <el-input 
                  v-model="editingValue" 
                  size="small" 
                  @keydown="handleEditKeydown"
                  autofocus
                  class="seamless-input"
                />
              </template>
              <span v-else>{{ scope.row.city }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="网址地址" min-width="150">
          <template #default="scope">
            <el-link 
              v-if="scope.row.websiteUrl" 
              type="primary" 
              :href="scope.row.websiteUrl" 
              target="_blank"
              @click.stop
            >
              <el-icon><Link /></el-icon>
              访问网站
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="岗位名称" prop="positionName" min-width="150">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'positionName')}" @click.stop="handleCellClick(scope.row, {property: 'positionName', label: '岗位名称'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'positionName')">
                <el-input 
                  v-model="editingValue" 
                  size="small" 
                  @keydown="handleEditKeydown"
                  autofocus
                  class="seamless-input"
                />
              </template>
              <span v-else>{{ scope.row.positionName }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="投递进度" width="120">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'applicationStatus')}" @click.stop="handleCellClick(scope.row, {property: 'applicationStatus', label: '投递进度'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'applicationStatus')">
                <el-select 
                  v-model="editingValue" 
                  size="small" 
                  placeholder="请选择"
                  @change="saveEdit"
                  style="width: 100%"
                  autofocus
                  class="seamless-input"
                >
                  <el-option label="待投递" value="待投递" />
                  <el-option label="已投递" value="已投递" />
                  <el-option label="已被拒" value="已被拒" />
                  <el-option label="一面" value="一面" />
                  <el-option label="二面" value="二面" />
                  <el-option label="主管面" value="主管面" />
                  <el-option label="已发offer" value="已发offer" />
                  <el-option label="放弃offer" value="放弃offer" />
                </el-select>
              </template>
              <template v-else>
                <el-tag 
                  v-if="scope.row.applicationStatus" 
                  :type="getStatusType(scope.row.applicationStatus)"
                  size="small"
                  effect="light"
                  class="status-tag"
                >
                  {{ scope.row.applicationStatus }}
                </el-tag>
              </template>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="面试时间" width="180">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'interviewTime')}" @click.stop="handleCellClick(scope.row, {property: 'interviewTime', label: '面试时间'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'interviewTime')">
                <el-date-picker
                  v-model="editingValue"
                  size="small"
                  type="datetime"
                  placeholder="选择日期时间"
                  format="YYYY-MM-DD HH:mm"
                  value-format="YYYY-MM-DDTHH:mm:00"
                  style="width: 100%"
                  @change="saveEdit"
                  class="seamless-input"
                />
              </template>
              <div v-else>
                {{ formatDateTime(scope.row.interviewTime) }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="岗位待遇" width="120">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'salary')}" @click.stop="handleCellClick(scope.row, {property: 'salary', label: '岗位待遇'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'salary')">
                <el-input 
                  v-model="editingValue" 
                  size="small" 
                  @keydown="handleEditKeydown"
                  autofocus
                  class="seamless-input"
                />
              </template>
              <span v-else>{{ scope.row.salary }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="投递日期" width="120">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'applicationDate')}" @click.stop="handleCellClick(scope.row, {property: 'applicationDate', label: '投递日期'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'applicationDate')">
                <el-date-picker
                  v-model="editingValue"
                  size="small"
                  type="date"
                  placeholder="选择日期"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  style="width: 100%"
                  @change="saveEdit"
                  class="seamless-input"
                />
              </template>
              <span v-else>{{ formatDate(scope.row.applicationDate) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="已投时间" width="120">
          <template #default="scope">
            {{ calculateAppliedTime(scope.row.applicationTime) }}
          </template>
        </el-table-column>
        <el-table-column label="备注说明" prop="remarks" min-width="150">
          <template #default="scope">
            <div class="editable-cell" :class="{'cell-editing': isCellEditing(tableData.indexOf(scope.row), 'remarks')}" @click.stop="handleCellClick(scope.row, {property: 'remarks', label: '备注说明'}, $event.target, $event)">
              <template v-if="isCellEditing(tableData.indexOf(scope.row), 'remarks')">
                <el-input 
                  v-model="editingValue" 
                  size="small" 
                  @keydown="handleEditKeydown"
                  autofocus
                  class="seamless-input"
                />
              </template>
              <span v-else>{{ scope.row.remarks }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template #default="scope">
            <el-button
              type="danger"
              size="small"
              text
              @click.stop="handleDelete(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页器 -->
      <div class="pagination-container">
        <el-pagination
          layout="total, sizes, prev, pager, next"
          :total="total"
          :page-sizes="[10, 20, 30, 50]"
          :current-page="currentPage"
          :page-size="pageSize"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 仪表盘视图 -->
    <div v-else-if="currentView === 'dashboard'" class="dashboard-container">
      <div class="dashboard-layout">
        <!-- 左侧面试日历 -->
        <div class="dashboard-left">
          <div class="dashboard-card interview-calendar">
            <div class="card-header">
              <h3>面试时间表</h3>
            </div>
            <div class="card-body">
              <div class="calendar-wrapper">
                <!-- 日历组件 -->
                <div class="calendar-container">
                  <div class="calendar-title">
                    <span>{{ new Date().getFullYear() }}年{{ new Date().getMonth() + 1 }}月</span>
                  </div>
                  <div class="calendar-header">
                    <div>周一</div>
                    <div>周二</div>
                    <div>周三</div>
                    <div>周四</div>
                    <div>周五</div>
                    <div>周六</div>
                    <div>周日</div>
                  </div>
                  <div class="calendar-body">
                    <div 
                      v-for="(day, index) in calendarData" 
                      :key="index"
                      class="calendar-day"
                      :class="{
                        'other-month': !day.isCurrentMonth,
                        'today': day.isToday,
                        'has-interview': day.hasInterview
                      }"
                    >
                      <span class="day-number">{{ day.date }}</span>
                      <div v-if="day.hasInterview" class="interview-marker">
                        <span>面试</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 右侧统计信息 -->
        <div class="dashboard-right">
          <!-- 上方数据卡片 -->
          <div class="dashboard-row">
            <!-- 开始日期 -->
            <div class="dashboard-card stat-card">
              <div class="card-header">
                <h3>开始日期</h3>
              </div>
              <div class="card-body">
                <div class="stat-value-large">
                  <div class="icon-container">
                    <el-icon><Calendar /></el-icon>
                  </div>
                  <div class="value">{{ earliestApplicationDate || '暂无数据' }}</div>
                </div>
              </div>
            </div>
            
            <!-- 投递数量 -->
            <div class="dashboard-card stat-card">
              <div class="card-header">
                <h3>投递数量</h3>
              </div>
              <div class="card-body">
                <div class="stat-value-large">
                  <div class="icon-container">
                    <el-icon><Document /></el-icon>
                  </div>
                  <div class="value">{{ getTotalApplications() }}</div>
                </div>
              </div>
            </div>
            
            <!-- 投递天数 -->
            <div class="dashboard-card stat-card">
              <div class="card-header">
                <h3>投递天数</h3>
              </div>
              <div class="card-body">
                <div class="stat-value-large">
                  <div class="icon-container">
                    <el-icon><Timer /></el-icon>
                  </div>
                  <div class="value">{{ calculateApplicationDays() }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 下方投递状态统计 -->
          <div class="dashboard-card application-stats" v-loading="loadingStats">
            <div class="card-header">
              <h3>投递状态统计</h3>
            </div>
            <div class="card-body">
              <div class="stats-container">
                <div v-for="status in sortedApplicationStatuses" :key="status" class="stat-item">
                  <div class="stat-label">{{ status }}</div>
                  <div class="stat-bar-container">
                    <div class="stat-bar" :style="{ width: getStatBarWidth(applicationStats[status]) }"></div>
                    <div class="stat-value">{{ applicationStats[status] || 0 }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑记录' : '添加记录'"
      width="600px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
        label-position="right"
      >
        <el-form-item label="公司名称" prop="companyName">
          <el-input v-model="form.companyName" placeholder="请输入公司名称" />
        </el-form-item>
        <el-form-item label="城市" prop="city">
          <el-input v-model="form.city" placeholder="请输入城市" />
        </el-form-item>
        <el-form-item label="网址地址" prop="websiteUrl">
          <el-input v-model="form.websiteUrl" placeholder="请输入网址地址" />
        </el-form-item>
        <el-form-item label="岗位名称" prop="positionName">
          <el-input v-model="form.positionName" placeholder="请输入岗位名称" />
        </el-form-item>
        <el-form-item label="投递进度" prop="applicationStatus">
          <el-select v-model="form.applicationStatus" placeholder="请选择投递进度" style="width: 100%">
            <el-option label="待投递" value="待投递" />
            <el-option label="已投递" value="已投递" />
            <el-option label="已被拒" value="已被拒" />
            <el-option label="一面" value="一面" />
            <el-option label="二面" value="二面" />
            <el-option label="主管面" value="主管面" />
            <el-option label="已发offer" value="已发offer" />
            <el-option label="放弃offer" value="放弃offer" />
          </el-select>
        </el-form-item>
        <el-form-item label="面试时间" prop="interviewTime">
          <el-date-picker
            v-model="form.interviewTime"
            type="datetime"
            placeholder="请选择面试时间"
            format="YYYY-MM-DD HH:mm"
            value-format="YYYY-MM-DDTHH:mm:00"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="岗位待遇" prop="salary">
          <el-input v-model="form.salary" placeholder="请输入岗位待遇" />
        </el-form-item>
        <el-form-item label="投递日期" prop="applicationDate">
          <el-date-picker
            v-model="form.applicationDate"
            type="date"
            placeholder="请选择投递日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="备注说明" prop="remarks">
          <el-input
            v-model="form.remarks"
            type="textarea"
            rows="4"
            placeholder="请输入备注说明"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">确认</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox, FormInstance } from 'element-plus';
import { 
  Grid, 
  DataBoard, 
  Search, 
  Plus,
  Lock,
  Link,
  ArrowDown,
  Calendar,
  Flag,
  Document,
  Timer
} from '@element-plus/icons-vue';
import jobApplicationService, { JobApplication, QueryParams, PageResult } from '@/services/jobApplicationService';
import api from '@/services/api';

const router = useRouter();
const currentView = ref('list');
const searchText = ref('');
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);
const loading = ref(false);
const tableData = ref<JobApplication[]>([]);
const dialogVisible = ref(false);
const isEdit = ref(false);
const submitting = ref(false);
const formRef = ref<FormInstance>();

// 表单数据
const form = reactive<JobApplication>({
  companyName: '',
  city: '',
  websiteUrl: '',
  positionName: '',
  applicationStatus: '待投递',
  interviewTime: new Date().toISOString().slice(0, 16) + ':00',
  salary: '',
  applicationDate: new Date().toISOString().slice(0, 10),
  remarks: ''
});

// 表单验证规则
const rules = {
  companyName: [
    { required: true, message: '请输入公司名称', trigger: 'blur' }
  ],
  positionName: [
    { required: true, message: '请输入岗位名称', trigger: 'blur' }
  ],
  applicationStatus: [
    { required: true, message: '请选择投递进度', trigger: 'change' }
  ]
};

// 仪表盘数据
const applicationStats = ref<Record<string, number>>({});
const loadingStats = ref(false);
const countdownDays = ref('00');
const countdownHours = ref('00');
const countdownMinutes = ref('00');
const earliestApplicationDate = ref('');
const nextInterviewDate = ref<Date | null>(null);
const countdownInterval = ref<number | null>(null);
const interviewDates = ref<Set<string>>(new Set());

// 返回旧版资源管理页面
const returnToOldVersion = () => {
  router.push('/resources/management');
};

// 页面加载时获取数据
onMounted(() => {
  loadData();
  document.addEventListener('click', handleDocumentClick);
  
  // 如果初始视图是仪表盘，则加载仪表盘数据
  if (currentView.value === 'dashboard') {
    loadDashboardData();
  }
});

// 加载数据
const loadData = async () => {
  loading.value = true;
  try {
    const params: QueryParams = {
      page: currentPage.value, // 不再减1，直接使用当前页码，因为jobApplicationService会确保page>=1
      size: pageSize.value
    };
    
    if (searchText.value) {
      params.keyword = searchText.value;
    }
    
    const result = await jobApplicationService.getList(params);
    tableData.value = result.content;
    total.value = result.totalElements;
  } catch (error: any) {
    ElMessage.error(`加载数据失败: ${error.message}`);
  } finally {
    loading.value = false;
  }
};

// 处理搜索
const handleSearch = () => {
  currentPage.value = 1;
  loadData();
};

// 打开添加对话框
const openAddDialog = () => {
  isEdit.value = false;
  dialogVisible.value = true;
};

// 处理编辑
const handleEdit = (row: JobApplication) => {
  isEdit.value = true;
  Object.assign(form, row);
  dialogVisible.value = true;
};

// 处理删除
const handleDelete = (row: JobApplication) => {
  if (!row.id) return;
  
  ElMessageBox.confirm(
    `确定要删除 ${row.companyName} 的记录吗？`,
    '删除确认',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      await jobApplicationService.delete(row.id as number);
      ElMessage.success('删除成功');
      loadData();
    } catch (error: any) {
      ElMessage.error(`删除失败: ${error.message}`);
    }
  }).catch(() => {
    // 取消删除，不做处理
  });
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      submitting.value = true;
      try {
        if (isEdit.value && form.id) {
          // 获取原始数据
          const originalData = tableData.value.find(item => item.id === form.id);
          if (originalData) {
            // 创建一个只包含修改字段的对象
            const changedData: Partial<JobApplication> = {};
            
            // 遍历表单字段，比较是否有变化
            Object.keys(form).forEach(key => {
              const typedKey = key as keyof JobApplication;
              // 如果字段值发生了变化，则添加到更新对象中
              if (form[typedKey] !== originalData[typedKey]) {
                changedData[typedKey] = form[typedKey];
              }
            });
            
            // 只有当有字段变化时才发送更新请求
            if (Object.keys(changedData).length > 0) {
              await jobApplicationService.update(form.id, changedData);
              ElMessage.success('更新成功');
            } else {
              ElMessage.info('没有字段发生变化');
            }
          } else {
            // 如果找不到原始数据，则发送完整表单
            await jobApplicationService.update(form.id, form);
            ElMessage.success('更新成功');
          }
        } else {
          await jobApplicationService.create(form);
          ElMessage.success('添加成功');
        }
        dialogVisible.value = false;
        loadData();
      } catch (error: any) {
        ElMessage.error(`提交失败: ${error.message}`);
      } finally {
        submitting.value = false;
      }
    }
  });
};

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
  
  Object.assign(form, {
    companyName: '',
    city: '',
    websiteUrl: '',
    positionName: '',
    applicationStatus: '待投递',
    interviewTime: new Date().toISOString().slice(0, 16) + ':00',
    salary: '',
    applicationDate: new Date().toISOString().slice(0, 10),
    remarks: ''
  });
};

// 获取状态标签类型
const getStatusType = (status: string) => {
  const statusMap: Record<string, string> = {
    '待投递': 'info',
    '已投递': 'info',
    '已被拒': 'danger',
    '一面': 'warning',
    '二面': 'success',
    '三面': 'success',
    '主管面': 'primary',
    'HR面': 'primary',
    '已通过': 'success',
    '已拒绝': 'danger',
    '已发offer': 'success',
    '放弃offer': 'warning'
  };
  
  return statusMap[status] || 'info';
};

// 处理分页大小变化
const handleSizeChange = (size: number) => {
  pageSize.value = size;
  loadData();
};

// 处理页码变化
const handlePageChange = (page: number) => {
  currentPage.value = page;
  loadData();
};

// 处理行点击
const handleRowClick = (row: JobApplication) => {
  // 如果正在编辑某个单元格，则不处理行点击事件
  if (editingCell.value) return;
  
  // 可以添加详细信息查看功能
  console.log('行点击', row);
};

// 格式化日期时间
const formatDateTime = (dateTimeStr: string) => {
  if (!dateTimeStr) return '';
  
  // 处理带T的ISO格式日期
  const dateTime = dateTimeStr.replace('T', ' ').substring(0, 16);
  return dateTime;
};

// 格式化日期
const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) return '';
  return dateStr;
};

// 计算已投时间（从申请时间到现在）
const calculateAppliedTime = (applicationTime: string | undefined) => {
  if (!applicationTime) return '计算中...';
  
  try {
    const applyDate = new Date(applicationTime);
    const now = new Date();
    
    const diffMs = now.getTime() - applyDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return `${diffDays}天`;
    } else {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours > 0) {
        return `${diffHours}小时`;
      } else {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `${diffMinutes}分钟`;
      }
    }
  } catch (error) {
    return '计算中...';
  }
};

// 添加编辑相关的状态
const editingCell = ref<{row: number, property: string} | null>(null);
const editingValue = ref('');

// 处理单元格单击
const handleCellClick = (row: JobApplication, column: any, cell: HTMLElement, event: Event) => {
  // 阻止事件冒泡
  event.stopPropagation();
  
  // 忽略不可编辑的列
  const nonEditableColumns = ['操作', '网址地址', '已投时间'];
  if (nonEditableColumns.includes(column.label)) return;
  
  const rowIndex = tableData.value.findIndex(item => item.id === row.id);
  if (rowIndex === -1) return;
  
  // 如果已经在编辑其他单元格，先保存
  if (editingCell.value) {
    saveEdit();
  }
  
  editingCell.value = {
    row: rowIndex,
    property: column.property
  };
  
  // 设置当前编辑值
  editingValue.value = row[column.property as keyof JobApplication] as string || '';
};

// 保存编辑
const saveEdit = async () => {
  if (!editingCell.value) return;
  
  const { row, property } = editingCell.value;
  const currentRow = tableData.value[row];
  
  // 如果值没变，则不更新
  if (currentRow[property as keyof JobApplication] === editingValue.value) {
    editingCell.value = null;
    return;
  }
  
  // 只发送需要更新的字段
  const updateData = {
    [property]: editingValue.value
  };
  
  try {
    if (currentRow.id) {
      await jobApplicationService.update(currentRow.id, updateData);
      ElMessage.success('更新成功');
      
      // 更新本地数据
      tableData.value[row] = { 
        ...currentRow,
        [property]: editingValue.value 
      };
    }
  } catch (error: any) {
    ElMessage.error(`更新失败: ${error.message}`);
  } finally {
    editingCell.value = null;
  }
};

// 取消编辑
const cancelEdit = () => {
  editingCell.value = null;
};

// 监听全局点击事件关闭编辑框
const handleDocumentClick = (event: MouseEvent) => {
  if (editingCell.value) {
    const editingElement = document.querySelector('.cell-editing');
    // 如果点击的不是正在编辑的单元格，也不是其子元素，则保存编辑
    if (editingElement && !editingElement.contains(event.target as Node)) {
      // 检查点击的元素是否是el-select或el-date-picker的下拉菜单
      const isDropdownElement = (
        (event.target as Element)?.closest('.el-select-dropdown') || 
        (event.target as Element)?.closest('.el-picker-panel')
      );
      
      // 如果不是下拉菜单元素，则保存编辑
      if (!isDropdownElement) {
        saveEdit();
      }
    }
  }
};

// 在组件销毁时移除事件监听
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }
});

// 判断单元格是否处于编辑状态
const isCellEditing = (row: number, property: string) => {
  return editingCell.value && editingCell.value.row === row && editingCell.value.property === property;
};

// 处理编辑时的键盘事件
const handleEditKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    saveEdit();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    cancelEdit();
  }
};

// 加载仪表盘数据
const loadDashboardData = async () => {
  loadingStats.value = true;
  try {
    // 获取投递统计数据
    applicationStats.value = await jobApplicationService.getStats();
    
    // 获取所有申请记录，用于计算最早投递日期和下一次面试
    const allApplicationsResponse = await jobApplicationService.getList({ page: 1, size: 1000 });
    const allApplications = allApplicationsResponse.content;
    
    // 计算最早投递日期
    if (allApplications.length > 0) {
      const dates = allApplications
        .filter(app => app.applicationDate)
        .map(app => new Date(app.applicationDate as string).getTime());
      
      if (dates.length > 0) {
        const earliestDate = new Date(Math.min(...dates));
        earliestApplicationDate.value = earliestDate.toISOString().split('T')[0];
      }
    }
    
    // 查找下一次面试
    const now = new Date();
    const upcomingInterviews = allApplications
      .filter(app => app.interviewTime && new Date(app.interviewTime) > now)
      .sort((a, b) => {
        const dateA = new Date(a.interviewTime as string);
        const dateB = new Date(b.interviewTime as string);
        return dateA.getTime() - dateB.getTime();
      });
    
    if (upcomingInterviews.length > 0) {
      nextInterviewDate.value = new Date(upcomingInterviews[0].interviewTime as string);
      startCountdown();
    } else {
      countdownDays.value = '00';
      countdownHours.value = '00';
      countdownMinutes.value = '00';
    }
    
    // 更新日历中的面试标记
    updateCalendarInterviews(allApplications);
    
  } catch (error: any) {
    ElMessage.error(`加载仪表盘数据失败: ${error.message}`);
  } finally {
    loadingStats.value = false;
  }
};

// 更新日历中的面试标记
const updateCalendarInterviews = (applications: JobApplication[]) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  // 清空之前的面试日期
  interviewDates.value.clear();
  
  // 找出当月的所有面试
  applications.forEach(app => {
    if (!app.interviewTime) return;
    
    const interviewDate = new Date(app.interviewTime);
    if (interviewDate.getFullYear() === currentYear && 
        interviewDate.getMonth() === currentMonth) {
      // 存储面试日期，格式为 YYYY-MM-DD
      const dateKey = interviewDate.toISOString().split('T')[0];
      interviewDates.value.add(dateKey);
    }
  });
};

// 开始倒计时
const startCountdown = () => {
  // 清除之前的定时器
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }
  
  // 设置新的定时器
  countdownInterval.value = window.setInterval(() => {
    if (!nextInterviewDate.value) return;
    
    const now = new Date();
    const diff = nextInterviewDate.value.getTime() - now.getTime();
    
    if (diff <= 0) {
      // 面试时间已到
      countdownDays.value = '00';
      countdownHours.value = '00';
      countdownMinutes.value = '00';
      clearInterval(countdownInterval.value as number);
      return;
    }
    
    // 计算天、时、分
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    countdownDays.value = days.toString().padStart(2, '0');
    countdownHours.value = hours.toString().padStart(2, '0');
    countdownMinutes.value = minutes.toString().padStart(2, '0');
  }, 1000);
};

// 计算统计条的宽度
const getStatBarWidth = (count: number) => {
  const total = Object.values(applicationStats.value).reduce((sum, val) => sum + val, 0);
  if (total === 0) return '0%';
  
  // 如果count为undefined，返回0%
  if (count === undefined) return '0%';
  
  return `${(count / total) * 100}%`;
};

// 生成日历数据
const calendarData = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  // 获取当月第一天
  const firstDay = new Date(year, month, 1);
  // 获取当月最后一天
  const lastDay = new Date(year, month + 1, 0);
  
  // 当月第一天是星期几（0-6，0是星期日）
  let firstDayOfWeek = firstDay.getDay();
  // 调整为星期一为一周的第一天
  firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  
  // 日历数据
  const days = [];
  
  // 添加上个月的日期
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = 0; i < firstDayOfWeek; i++) {
    days.push({
      date: prevMonthLastDay - firstDayOfWeek + i + 1,
      isCurrentMonth: false,
      hasInterview: false
    });
  }
  
  // 添加当月的日期
  for (let i = 1; i <= lastDay.getDate(); i++) {
    // 检查这一天是否有面试
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    const hasInterview = interviewDates.value.has(dateStr);
    
    days.push({
      date: i,
      isCurrentMonth: true,
      hasInterview: hasInterview,
      isToday: i === now.getDate()
    });
  }
  
  // 添加下个月的日期
  const remainingDays = 42 - days.length; // 6行7列 = 42
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      date: i,
      isCurrentMonth: false,
      hasInterview: false
    });
  }
  
  return days;
});

// 计算投递数量
const getTotalApplications = () => {
  return Object.values(applicationStats.value).reduce((sum, count) => sum + count, 0);
};

// 计算投递天数
const calculateApplicationDays = () => {
  if (!earliestApplicationDate.value) {
    return 0;
  }
  
  const now = new Date();
  const startDate = new Date(earliestApplicationDate.value);
  const diffTime = Math.abs(now.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays || 0;
};

// 处理视图切换
const handleViewChange = (view: string) => {
  if (view === 'dashboard') {
    loadDashboardData();
  } else if (view === 'list') {
    loadData();
  }
};

// 添加排序投递状态的逻辑，按照指定顺序：待投递，已投递，已被拒，一面，二面，主管面，已发offer，放弃offer
const statusOrder = ['待投递', '已投递', '已被拒', '一面', '二面', '主管面', '已发offer', '放弃offer'];

// 排序后的投递状态
const sortedApplicationStatuses = computed(() => {
  // 确保所有预定义状态都存在，即使数量为0
  const result = [...statusOrder];
  
  // 添加可能存在但不在预定义列表中的其他状态
  const otherStatuses = Object.keys(applicationStats.value).filter(
    status => !statusOrder.includes(status)
  );
  
  if (otherStatuses.length > 0) {
    // 按字母顺序排序其他状态
    otherStatuses.sort((a, b) => a.localeCompare(b));
    result.push(...otherStatuses);
  }
  
  return result;
});
</script>

<style scoped lang="scss">
.simple-resource-management {
  padding: 32px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);

  .page-header {
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 85%;
    margin: 0 auto 32px;

    .header-main {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .header-left {
      text-align: left;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
    }
  }

  .page-title {
    margin: 0;
    font-size: 2rem;
    font-weight: 600;
    color: #303133;
  }

  .page-description {
    margin: 8px 0 0;
    font-size: 1rem;
    color: #606266;
  }

  .search-box {
    position: relative;
    flex: 1;
  }

  .rounded-search {
    width: 100%;
  }

  .rounded-search :deep(.el-input__wrapper) {
    border-radius: 20px;
    padding-right: 40px;
    height: 36px;
  }

  .search-icon {
    color: #909399;
    margin-right: 4px;
    font-size: 18px;
  }

  .search-action-button {
    height: 28px;
    width: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    border-color: transparent;
    background-color: transparent;
    margin-right: -8px;
  }

  .resource-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .el-button {
      border-radius: 20px;
      padding: 0 20px;
      height: 36px;
    }
  }

  .view-toggle {
    margin-right: 16px;
    
    .custom-radio-group {
      .rounded-button {
        :deep(.el-radio-button__inner) {
          border-radius: 20px;
          padding: 0 20px;
          margin: 0 5px;
          height: 36px;
          line-height: 36px;
        }
        
        &:first-child {
          :deep(.el-radio-button__inner) {
            border-radius: 20px;
          }
        }
        
        &:last-child {
          :deep(.el-radio-button__inner) {
            border-radius: 20px;
          }
        }
      }
    }
  }

  .table-container {
    background-color: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    width: 85%;
    margin: 0 auto;
    
    // 调整选择框位置
    :deep(.selection-column) .el-checkbox {
      margin-left: 8px; // 向右移动选择框
    }
    
    .rounded-table {
      // 设置表格圆角
      :deep(.el-table__inner-wrapper),
      :deep(.el-table__header-wrapper),
      :deep(.el-table__body-wrapper),
      :deep(.el-table__footer-wrapper) {
        border-radius: 8px;
        overflow: hidden;
      }
      
      // 设置表格边框
      :deep(.el-table__border-left-patch) {
        background-color: #f5f7fa;
      }
      
      // 设置表头样式
      :deep(th.el-table__cell) {
        font-weight: 600;
        padding: 12px 0;
      }
      
      // 设置表格行样式
      :deep(.el-table__row) {
        height: 48px;
        
        &:hover {
          background-color: #f5faff;
        }
      }
      
      // 设置单元格样式
      :deep(td.el-table__cell) {
        padding: 0;
        
        .cell {
          padding: 0;
          height: 100%;
          display: flex;
          align-items: center;
        }
      }
      
      // 可编辑单元格样式
      .editable-cell {
        padding: 5px;
        border-radius: 4px;
        min-height: 24px;
        cursor: pointer;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        font-size: 14px;
        line-height: 1.5;
        
        span, div {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        &:hover:not(.cell-editing) {
          background-color: #f5faff;
        }
        
        &.cell-editing {
          padding: 0;
          
          :deep(.el-input__wrapper),
          :deep(.el-select),
          :deep(.el-date-editor) {
            width: 100%;
            height: 100%;
            box-shadow: none;
          }
          
          :deep(.el-input__inner) {
            border: none;
            background-color: transparent;
            font-size: 14px;
            line-height: 1.5;
          }
        }
      }
    }
    
    .status-tag {
      border-radius: 12px;
      padding: 0 10px;
      height: 24px;
      line-height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  @media (max-width: 768px) {
    padding: 0;
    background-color: #f5f5f7;
    
    .page-header {
      padding: 15px;
      margin: 10px auto;
      width: calc(100% - 20px);
      border-radius: 12px;
      background-color: #fff;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
      
      .header-main {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        padding: 0;
      }

      .header-actions {
        flex-direction: column;
        width: 100%;
        gap: 10px;
        padding: 0;
      }

      .view-toggle {
        width: 100%;
        margin-right: 0;

        .custom-radio-group {
          width: 100%;
          display: flex;

          :deep(.el-radio-button) {
            flex: 1;

            .el-radio-button__inner {
              width: 100%;
              justify-content: center;
            }
          }
        }
      }

      .search-box {
        width: 100%;
      }

      .resource-actions {
        width: 100%;
        
        .el-button {
          width: 100%;
        }
      }
    }

    .table-container {
      width: calc(100% - 20px);
      margin: 10px auto;
      padding: 10px;
      overflow-x: auto;
      
      // 移动端表格滚动
      .rounded-table {
        width: 100%;
        
        :deep(.el-table__body-wrapper) {
          overflow-x: auto;
        }
      }
      
      .pagination-container {
        justify-content: center;
      }
    }
  }
}

.dashboard-container {
  width: 85%;
  margin: 0 auto;
  padding: 32px 0;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);

  .dashboard-layout {
    display: flex;
    gap: 24px;
    margin-bottom: 24px;

    .dashboard-left {
      flex: 1;
      display: flex;
      flex-direction: column;
      
      .interview-calendar {
        background-color: #fff;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
        height: 100%;
        
        .card-header {
          margin-bottom: 20px;
          
          h3 {
            font-size: 1.2rem;
            font-weight: 600;
            color: #303133;
            margin: 0;
          }
        }
        
        .calendar-wrapper {
          .calendar-container {
            width: 100%;
            
            .calendar-title {
              font-size: 1.1rem;
              font-weight: 500;
              margin-bottom: 15px;
              text-align: center;
            }
            
            .calendar-header {
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              text-align: center;
              font-weight: 500;
              margin-bottom: 10px;
              
              div {
                padding: 8px 0;
                color: #606266;
              }
            }
            
            .calendar-body {
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              grid-gap: 2px;
              
              .calendar-day {
                aspect-ratio: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                position: relative;
                border-radius: 4px;
                cursor: pointer;
                
                &:hover {
                  background-color: #f5f7fa;
                }
                
                .day-number {
                  font-size: 1rem;
                  font-weight: 500;
                }
                
                &.other-month {
                  color: #c0c4cc;
                }
                
                &.today {
                  background-color: #ecf5ff;
                  color: #409eff;
                  font-weight: bold;
                }
                
                &.has-interview {
                  background-color: #f0f9eb;
                  
                  .interview-marker {
                    font-size: 0.7rem;
                    color: #67c23a;
                    margin-top: 4px;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    .dashboard-right {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 24px;
      
      .dashboard-row {
        display: flex;
        gap: 24px;
        margin-bottom: 0;
        
        .stat-card {
          flex: 1;
          background-color: #fff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
          
          .card-header {
            margin-bottom: 10px;
            
            h3 {
              font-size: 1rem;
              font-weight: 600;
              color: #303133;
              margin: 0;
            }
          }
          
          .stat-value-large {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            
            .icon-container {
              font-size: 2rem;
              color: #409eff;
              margin-bottom: 10px;
            }
            
            .value {
              font-size: 1.5rem;
              font-weight: 600;
              color: #303133;
            }
          }
        }
      }
      
      .application-stats {
        flex: 1;
        background-color: #fff;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
        
        .card-header {
          margin-bottom: 20px;
          
          h3 {
            font-size: 1.2rem;
            font-weight: 600;
            color: #303133;
            margin: 0;
          }
        }
        
        .stats-container {
          .stat-item {
            margin-bottom: 15px;
            
            .stat-label {
              font-size: 0.9rem;
              color: #606266;
              margin-bottom: 5px;
            }
            
            .stat-bar-container {
              display: flex;
              align-items: center;
              height: 20px;
              
              .stat-bar {
                height: 100%;
                background-color: #409eff;
                border-radius: 10px;
                transition: width 0.3s ease;
              }
              
              .stat-value {
                margin-left: 10px;
                font-size: 0.9rem;
                color: #606266;
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    width: calc(100% - 20px);
    margin: 10px auto;
    padding: 10px;
    
    .dashboard-layout {
      flex-direction: column;
      gap: 16px;
      
      .dashboard-right {
        gap: 16px;
        
        .dashboard-row {
          flex-direction: column;
          gap: 16px;
        }
      }
    }
  }
}

.dashboard-row {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;

  .dashboard-card {
    flex: 1;
    background-color: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);

    .card-header {
      margin-bottom: 20px;
      
      h3 {
        font-size: 1.2rem;
        font-weight: 600;
        color: #303133;
        margin: 0;
      }
    }

    .card-body {
      min-height: 100px;
    }
  }
}

.view-toggle {
  margin-right: 16px;
  
  .custom-radio-group {
    .rounded-button {
      :deep(.el-radio-button__inner) {
        border-radius: 20px;
        padding: 0 20px;
        margin: 0 5px;
        height: 36px;
        line-height: 36px;
      }
      
      &:first-child {
        :deep(.el-radio-button__inner) {
          border-radius: 20px;
        }
      }
      
      &:last-child {
        :deep(.el-radio-button__inner) {
          border-radius: 20px;
        }
      }
    }
  }
}

.seamless-input {
  width: 100%;
  height: 100%;
  font-size: inherit;
  
  :deep(.el-input__wrapper) {
    box-shadow: none;
    padding: 0 5px;
    background-color: #f5faff;
    height: 100%;
  }
  
  :deep(.el-input__inner) {
    height: 100%;
    border: none;
    padding: 0;
    font-size: inherit;
    line-height: inherit;
  }
  
  :deep(.el-select__wrapper) {
    box-shadow: none;
    height: 100%;
  }
  
  :deep(.el-date-editor .el-input__wrapper) {
    padding: 0 5px;
    height: 100%;
  }
  
  :deep(.el-select__input) {
    font-size: inherit;
  }
}

:deep(.el-select-dropdown__item) {
  font-size: 14px;
  line-height: 1.5;
  padding: 8px 12px;
}

:deep(.el-date-table td) {
  font-size: 14px;
}

:deep(.el-picker-panel) {
  font-size: 14px;
}
</style> 